/**
 * Tip Payment Modal — CC-style card/USD version (MiniCaryn)
 * Dark bottom-sheet for confirming a tip. Card rails: saved cards
 * (SavedCardSelector) + new card (Stripe CardElement), with the wallet as an
 * OPTIONAL USD offset. Accents reskinned to Mini blue, pure USD ($).
 *
 * Two-step flow preserved: initiate (charge) -> send (deliver). If the charge
 * succeeds but send fails, the error is SURFACED — never a silent retry.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { getPaymentMethods } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import SavedCardSelector from '../wallet/SavedCardSelector'
import ReVerifyCardBanner from '../wallet/ReVerifyCardBanner'
import { usePreviousCard } from '@/hooks/usePreviousCard'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface TipPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  amount: number
  recipientId: string
  conversationId: string
}

export function TipPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  amount,
  recipientId,
  conversationId
}: TipPaymentModalProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Wallet state (optional USD offset)
  const [walletBalance, setWalletBalance] = useState<number>(0)
  const [useWallet, setUseWallet] = useState(true)

  // Saved cards state
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [saveCard, setSaveCard] = useState(true)
  const [isLoadingCards, setIsLoadingCards] = useState(true)

  // Display-only "re-verify your card" hint (does not affect payment logic)
  const { card: previousCard } = usePreviousCard(isOpen)

  // Load wallet balance and saved cards when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null)
      setIsLoadingCards(true)
      fetchSavedCards()
      fetchWalletBalance()
    }
  }, [isOpen])

  // Dismiss keyboard when modal opens (mobile support)
  useEffect(() => {
    if (isOpen) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [isOpen])

  async function fetchWalletBalance() {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${API_URL}/api/wallet/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setWalletBalance(data.balance || 0)
      }
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err)
      setWalletBalance(0)
    }
  }

  async function fetchSavedCards() {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        setIsLoadingCards(false)
        return
      }

      const response = await getPaymentMethods(token)
      setSavedCards(response.payment_methods || [])

      // Auto-select default card if exists
      const defaultCard = response.payment_methods?.find((pm: PaymentMethod) => pm.is_default)
      if (defaultCard) {
        setSelectedCardId(defaultCard.id)
      }
    } catch (err) {
      console.error('[TipPaymentModal] Failed to fetch saved cards:', err)
      setSavedCards([])
      setSelectedCardId(null)
    } finally {
      setIsLoadingCards(false)
    }
  }

  async function sendTipMessage(paymentIntentId: string) {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_URL}/api/messages/tip/send`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        recipient_id: recipientId,
        amount: amount,
        payment_intent_id: paymentIntentId
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('[TipPaymentModal] Failed to send tip:', errorData)
      throw new Error(errorData.error || 'Failed to send tip')
    }

    const data = await response.json()
    return data
  }

  async function handlePurchase() {
    try {
      setIsProcessing(true)
      setError(null)

      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('Authentication required')
      }

      // Calculate wallet vs card split (USD)
      const walletAmount = useWallet ? Math.min(walletBalance, amount) : 0
      const fullWalletPayment = walletAmount >= amount

      // If wallet covers the full amount, no card needed
      if (fullWalletPayment) {
        const response = await fetch(`${API_URL}/api/messages/tip/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_id: recipientId,
            amount: amount,
            use_wallet: true,
            wallet_amount: walletAmount
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to process wallet payment')
        }

        const data = await response.json()

        try {
          await sendTipMessage(data.payment_intent_id)
          onSuccess()
          handleClose()
        } catch (sendError: any) {
          console.error('[Tip] Payment succeeded but send failed:', sendError)
          setError(`Payment successful but failed to send tip: ${sendError.message}`)
        }
        return
      }

      // Need card payment
      if (!stripe) {
        setError('Payment system not loaded')
        return
      }

      // Using saved payment method
      if (savedCards.length > 0 && selectedCardId && selectedCardId !== 'new') {
        const selectedCard = savedCards.find(card => card.id === selectedCardId)
        if (!selectedCard) {
          throw new Error('Selected payment method not found')
        }

        const response = await fetch(`${API_URL}/api/messages/tip/initiate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient_id: recipientId,
            amount: amount,
            saved_payment_method_id: selectedCard.stripe_payment_method_id,
            use_wallet: useWallet && walletAmount > 0,
            wallet_amount: walletAmount
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to initiate payment')
        }

        const data = await response.json()

        try {
          await sendTipMessage(data.payment_intent_id)
          onSuccess()
          handleClose()
        } catch (sendError: any) {
          console.error('[Tip] Payment succeeded but send failed:', sendError)
          setError(`Payment successful but failed to send tip: ${sendError.message}`)
        }
        return
      }

      // Using new card
      if (!elements) {
        setError('Payment form not loaded')
        return
      }

      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        setError('Please enter your card details')
        return
      }

      const response = await fetch(`${API_URL}/api/messages/tip/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: recipientId,
          amount: amount,
          save_payment_method: saveCard,
          use_wallet: useWallet && walletAmount > 0,
          wallet_amount: walletAmount
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to initiate payment')
      }

      const { client_secret, payment_intent_id } = await response.json()

      // Confirm payment with new card
      const { error: confirmError } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement
        }
      })

      if (confirmError) {
        throw new Error(confirmError.message)
      }

      try {
        await sendTipMessage(payment_intent_id)
        onSuccess()
        handleClose()
      } catch (sendError: any) {
        console.error('[Tip] Payment succeeded but send failed:', sendError)
        setError(`Payment successful but failed to send tip: ${sendError.message}`)
      }
    } catch (err: any) {
      console.error('Tip payment error:', err)
      setError(err.message || 'Payment failed')
    } finally {
      setIsProcessing(false)
    }
  }

  function handleClose() {
    setError(null)
    setSavedCards([])
    setSelectedCardId(null)
    setIsLoadingCards(true)
    onClose()
  }

  if (!isOpen) return null

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1f2937',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        '::placeholder': {
          color: '#9ca3af'
        }
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444'
      }
    }
  }

  // Format amount display (pure USD)
  const formatAmount = (amt: number) => {
    if (amt % 1 === 0) return `$${amt}`
    return `$${amt.toFixed(2)}`
  }

  const walletAmount = useWallet ? Math.min(walletBalance, amount) : 0
  const fullWalletPayment = walletAmount >= amount

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col justify-end"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        touchAction: 'none'
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={handleClose}
      />

      {/* Bottom sheet modal */}
      <div
        className="relative w-full rounded-t-3xl overflow-hidden animate-slide-up flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #1c1c1e 0%, #0a0a0a 100%)',
          maxHeight: 'calc(100vh - env(safe-area-inset-top, 20px) - 60px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="pt-4 pb-3 px-6 flex items-center justify-between border-b border-white/10">
          <button
            onClick={handleClose}
            className="text-[#5BB4D6] text-base font-normal"
            disabled={isProcessing}
          >
            Cancel
          </button>
          <span className="text-white font-semibold text-lg">Confirm Tip</span>
          <div className="w-14" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoadingCards ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-white/20 border-t-white"></div>
              <p className="mt-4 text-gray-400">Loading payment methods...</p>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-[#5BB4D6] text-sm"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Amount Display */}
              <div className="text-center py-4">
                <span className="text-4xl font-bold text-white">
                  {formatAmount(amount)}
                </span>
              </div>

              {/* Store Credit (optional USD offset) */}
              {walletBalance > 0 && (
                <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💰</span>
                      <div>
                        <p className="text-white text-sm font-medium">Store Credit</p>
                        <p className="text-gray-400 text-xs">${walletBalance.toFixed(2)}</p>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <div
                        onClick={() => setUseWallet(!useWallet)}
                        className={`w-12 h-7 rounded-full transition-colors relative cursor-pointer ${
                          useWallet ? 'bg-[#30d158]' : 'bg-[#3a3a3c]'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                            useWallet ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </div>
                    </label>
                  </div>
                  {useWallet && (
                    <p className="text-gray-400 text-xs mt-2">
                      {fullWalletPayment
                        ? 'Full payment from store credit'
                        : `${formatAmount(walletAmount)} from store credit + ${formatAmount(amount - walletAmount)} from card`
                      }
                    </p>
                  )}
                </div>
              )}

              {/* Card Section - only if wallet doesn't cover */}
              {!fullWalletPayment && (
                <>
                  {/* Saved Cards Selector */}
                  {savedCards.length > 0 && (
                    <div className="mb-4 bg-gray-300 rounded-xl p-3">
                      <SavedCardSelector
                        paymentMethods={savedCards}
                        selectedId={selectedCardId}
                        onSelect={setSelectedCardId}
                        onAddNew={() => setSelectedCardId('new')}
                      />
                    </div>
                  )}

                  {/* New Card Form */}
                  {(!savedCards.length || selectedCardId === 'new') && (
                    <div className="mb-4">
                      <ReVerifyCardBanner card={previousCard} variant="dark" />
                      <p className="text-gray-400 text-xs mb-2 uppercase tracking-wider">
                        {previousCard?.last4 ? 'Re-verify your card' : 'Card Details'}
                      </p>
                      <div className="p-4 rounded-xl bg-gray-300">
                        <CardElement options={CARD_ELEMENT_OPTIONS} />
                      </div>
                      <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={saveCard}
                          onChange={(e) => setSaveCard(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-400 text-[#4AA3CC] bg-gray-600"
                        />
                        <span className="text-gray-400 text-sm">Save for future</span>
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Send Button */}
        {!error && !isLoadingCards && (
          <div className="px-6 pb-8 pt-2">
            <button
              onClick={handlePurchase}
              disabled={!stripe || isProcessing}
              className="w-full py-4 rounded-xl font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-40"
              style={{
                background: 'linear-gradient(135deg, #5BB4D6 0%, #4AA3CC 100%)',
                color: '#ffffff'
              }}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </span>
              ) : (
                `Send ${formatAmount(amount)}`
              )}
            </button>
          </div>
        )}

        {/* Home indicator bar */}
        <div className="pb-2 flex justify-center">
          <div className="w-32 h-1 bg-white/20 rounded-full" />
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  )

  return createPortal(modalContent, document.body)
}
